import React, { useCallback, useEffect, useState } from "react";
import Header from "../../components/Header/Header";
import adminApi, {
  OrganizationRoleDto,
  OrganizationUserDto,
} from "../../Api/admin.api";
import { savePathToLocalStorage } from "../../utils/helpers";

type Tab = "roles" | "users";

const OrganizationAdmin = () => {
  savePathToLocalStorage();

  const [tab, setTab] = useState<Tab>("roles");
  const [roles, setRoles] = useState<OrganizationRoleDto[]>([]);
  const [users, setUsers] = useState<OrganizationUserDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [newRoleName, setNewRoleName] = useState("");
  const [newRoleDesc, setNewRoleDesc] = useState("");

  const [editingRoleId, setEditingRoleId] = useState<number | null>(null);
  const [editRoleName, setEditRoleName] = useState("");
  const [editRoleDesc, setEditRoleDesc] = useState("");

  const [userEditId, setUserEditId] = useState<number | null>(null);
  const [userRoleSelection, setUserRoleSelection] = useState<number[]>([]);

  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserFullname, setNewUserFullname] = useState("");
  const [newUserPassword, setNewUserPassword] = useState("");
  const [newUserPassword2, setNewUserPassword2] = useState("");

  const loadRoles = useCallback(async () => {
    const data = await adminApi.getRoles();
    setRoles(data);
  }, []);

  const loadUsers = useCallback(async () => {
    const data = await adminApi.getUsers();
    setUsers(data);
  }, []);

  const refresh = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      await Promise.all([loadRoles(), loadUsers()]);
    } catch (e: any) {
      setError(e?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [loadRoles, loadUsers]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const onCreateRole = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newRoleName.trim()) return;
    setError(null);
    try {
      await adminApi.createRole({
        name: newRoleName.trim(),
        description: newRoleDesc.trim() || null,
      });
      setNewRoleName("");
      setNewRoleDesc("");
      await loadRoles();
    } catch (err: any) {
      setError(err?.message || "Could not create role");
    }
  };

  const startEditRole = (r: OrganizationRoleDto) => {
    setEditingRoleId(r.id);
    setEditRoleName(r.name);
    setEditRoleDesc(r.description || "");
  };

  const saveEditRole = async () => {
    if (editingRoleId == null || !editRoleName.trim()) return;
    setError(null);
    try {
      await adminApi.updateRole(editingRoleId, {
        name: editRoleName.trim(),
        description: editRoleDesc.trim() || null,
      });
      setEditingRoleId(null);
      await loadRoles();
    } catch (err: any) {
      setError(err?.message || "Could not update role");
    }
  };

  const removeRole = async (id: number) => {
    if (!window.confirm("Delete this role? It will be unassigned from all users.")) {
      return;
    }
    setError(null);
    try {
      await adminApi.deleteRole(id);
      await Promise.all([loadRoles(), loadUsers()]);
    } catch (err: any) {
      setError(err?.message || "Could not delete role");
    }
  };

  const startUserRoles = (u: OrganizationUserDto) => {
    setUserEditId(u.id);
    setUserRoleSelection(u.roles.map((r) => r.id));
  };

  const toggleRoleInSelection = (roleId: number) => {
    setUserRoleSelection((prev) =>
      prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId]
    );
  };

  const saveUserRoles = async () => {
    if (userEditId == null) return;
    setError(null);
    try {
      const updated = await adminApi.setUserRoles(userEditId, userRoleSelection);
      setUsers((list) =>
        list.map((u) => (u.id === updated.id ? updated : u))
      );
      setUserEditId(null);
    } catch (err: any) {
      setError(err?.message || "Could not update user roles");
    }
  };

  const onCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = newUserEmail.trim().toLowerCase();
    if (!email) {
      setError("Email is required");
      return;
    }
    if (!newUserPassword || newUserPassword.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    if (newUserPassword !== newUserPassword2) {
      setError("Passwords do not match");
      return;
    }
    setError(null);
    try {
      await adminApi.createUser({
        email,
        password: newUserPassword,
        fullname: newUserFullname.trim() || null,
      });
      setNewUserEmail("");
      setNewUserFullname("");
      setNewUserPassword("");
      setNewUserPassword2("");
      await loadUsers();
    } catch (err: any) {
      setError(err?.message || "Could not create user");
    }
  };

  const btnStyle = (active: boolean): React.CSSProperties => ({
    padding: "8px 16px",
    marginRight: 8,
    borderRadius: 4,
    border: "1px solid #ccc",
    background: active ? "#1976d2" : "#fff",
    color: active ? "#fff" : "#333",
    cursor: "pointer",
  });

  return (
    <div className="organization-admin main-content">
      <Header title="Organization" />
      <div style={{ padding: "1rem 2rem" }}>
        <p style={{ marginBottom: "1rem", opacity: 0.85 }}>
          Manage roles and assign them to users in your organization.
        </p>

        <div style={{ marginBottom: "1.5rem" }}>
          <button type="button" style={btnStyle(tab === "roles")} onClick={() => setTab("roles")}>
            Roles
          </button>
          <button type="button" style={btnStyle(tab === "users")} onClick={() => setTab("users")}>
            Users
          </button>
        </div>

        {error && (
          <div
            style={{
              padding: "10px 12px",
              marginBottom: 12,
              background: "#ffebee",
              color: "#c62828",
              borderRadius: 4,
            }}
          >
            {error}
          </div>
        )}

        {loading ? (
          <p>Loading…</p>
        ) : tab === "roles" ? (
          <>
            <form
              onSubmit={onCreateRole}
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
                alignItems: "flex-end",
                marginBottom: 24,
              }}
            >
              <div>
                <label style={{ display: "block", fontSize: 12 }}>Role name</label>
                <input
                  value={newRoleName}
                  onChange={(e) => setNewRoleName(e.target.value)}
                  style={{ minWidth: 200, padding: 8 }}
                />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 12 }}>Description (optional)</label>
                <input
                  value={newRoleDesc}
                  onChange={(e) => setNewRoleDesc(e.target.value)}
                  style={{ minWidth: 260, padding: 8 }}
                />
              </div>
              <button
                type="submit"
                style={{ padding: "8px 16px", cursor: "pointer" }}
              >
                Add role
              </button>
            </form>

            <table style={{ borderCollapse: "collapse", width: "100%", maxWidth: 800 }}>
              <thead>
                <tr style={{ textAlign: "left", borderBottom: "1px solid #ddd" }}>
                  <th style={{ padding: 8 }}>Name</th>
                  <th style={{ padding: 8 }}>Description</th>
                  <th style={{ padding: 8 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {roles.map((r) => (
                  <tr key={r.id} style={{ borderBottom: "1px solid #eee" }}>
                    <td style={{ padding: 8 }}>
                      {editingRoleId === r.id ? (
                        <input
                          value={editRoleName}
                          onChange={(e) => setEditRoleName(e.target.value)}
                          style={{ width: "100%", padding: 6 }}
                        />
                      ) : (
                        r.name
                      )}
                    </td>
                    <td style={{ padding: 8 }}>
                      {editingRoleId === r.id ? (
                        <input
                          value={editRoleDesc}
                          onChange={(e) => setEditRoleDesc(e.target.value)}
                          style={{ width: "100%", padding: 6 }}
                        />
                      ) : (
                        r.description || "—"
                      )}
                    </td>
                    <td style={{ padding: 8 }}>
                      {editingRoleId === r.id ? (
                        <>
                          <button type="button" onClick={saveEditRole} style={{ marginRight: 8 }}>
                            Save
                          </button>
                          <button type="button" onClick={() => setEditingRoleId(null)}>
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button type="button" onClick={() => startEditRole(r)} style={{ marginRight: 8 }}>
                            Edit
                          </button>
                          <button type="button" onClick={() => removeRole(r.id)}>
                            Delete
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
                {roles.length === 0 && (
                  <tr>
                    <td colSpan={3} style={{ padding: 12 }}>
                      No roles yet. Create one above.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </>
        ) : (
          <>
            <form
              onSubmit={onCreateUser}
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
                alignItems: "flex-end",
                marginBottom: 24,
              }}
            >
              <div>
                <label style={{ display: "block", fontSize: 12 }}>Email</label>
                <input
                  type="email"
                  autoComplete="off"
                  value={newUserEmail}
                  onChange={(e) => setNewUserEmail(e.target.value)}
                  style={{ minWidth: 220, padding: 8 }}
                />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 12 }}>Full name (optional)</label>
                <input
                  value={newUserFullname}
                  onChange={(e) => setNewUserFullname(e.target.value)}
                  style={{ minWidth: 180, padding: 8 }}
                />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 12 }}>Password</label>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={newUserPassword}
                  onChange={(e) => setNewUserPassword(e.target.value)}
                  style={{ minWidth: 160, padding: 8 }}
                />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 12 }}>Confirm password</label>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={newUserPassword2}
                  onChange={(e) => setNewUserPassword2(e.target.value)}
                  style={{ minWidth: 160, padding: 8 }}
                />
              </div>
              <button type="submit" style={{ padding: "8px 16px", cursor: "pointer" }}>
                Add user
              </button>
            </form>

            <table style={{ borderCollapse: "collapse", width: "100%", maxWidth: 900 }}>
              <thead>
                <tr style={{ textAlign: "left", borderBottom: "1px solid #ddd" }}>
                  <th style={{ padding: 8 }}>Email</th>
                  <th style={{ padding: 8 }}>Name</th>
                  <th style={{ padding: 8 }}>Roles</th>
                  <th style={{ padding: 8 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} style={{ borderBottom: "1px solid #eee", verticalAlign: "top" }}>
                    <td style={{ padding: 8 }}>{u.email}</td>
                    <td style={{ padding: 8 }}>{u.fullname || "—"}</td>
                    <td style={{ padding: 8 }}>
                      {userEditId === u.id ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                          {roles.length === 0 ? (
                            <span style={{ fontSize: 13 }}>Create roles first (Roles tab).</span>
                          ) : (
                            roles.map((r) => (
                              <label key={r.id} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                <input
                                  type="checkbox"
                                  checked={userRoleSelection.includes(r.id)}
                                  onChange={() => toggleRoleInSelection(r.id)}
                                />
                                {r.name}
                              </label>
                            ))
                          )}
                        </div>
                      ) : u.roles.length > 0 ? (
                        u.roles.map((r) => r.name).join(", ")
                      ) : (
                        "—"
                      )}
                    </td>
                    <td style={{ padding: 8 }}>
                      {userEditId === u.id ? (
                        <>
                          <button type="button" onClick={saveUserRoles} style={{ marginRight: 8 }}>
                            Save
                          </button>
                          <button type="button" onClick={() => setUserEditId(null)}>
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button type="button" onClick={() => startUserRoles(u)}>
                          Edit roles
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr>
                    <td colSpan={4} style={{ padding: 12 }}>
                      No users in this organization yet. Add one above.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
};

export default OrganizationAdmin;
